import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import React from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useVenuePosIntegrationStatus, useExternalAccessRights } from 'common/hooks';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import { PageHeader } from 'modules/experience-core';
import { useExperienceModuleContext } from 'modules/experience-core';
import { useIsVenueLevelMenuEditorEnabled } from 'modules/menu/hooks/useIsVenueLevelMenuEditorEnabled';
import { useIsListingManagerBetaEnabled } from 'modules/shared-menu/hooks/useIsListingManagerBetaEnabled';
import { VenueLevelTranslationsPage } from 'modules/shared-menu/pages/VenueLevelTranslationsPage';

import Translation from './components/Translation';
import { useSharedMenuLink } from './hooks/useSharedMenuLink';

const SpinnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

export const ExperienceTranslation = () => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();
  const { selectedVenueExperienceEntity } = useExperienceModuleContext();
  const venue = selectedVenueExperienceEntity.experienceEntityObject as Venue;
  const { sharedMenuLink, isSharedMenuLinkLoading } = useSharedMenuLink(venue.id);
  const { isVenueLevelMenuEditorEnabled, isVenueLevelMenuEditorEnabledLoading } = useIsVenueLevelMenuEditorEnabled();
  const { isListingManagerBetaEnabled, isListingManagerBetaEnabledLoading } = useIsListingManagerBetaEnabled(venue);

  const { data: venuePosIntegrationStatus } = useVenuePosIntegrationStatus(venue.id);

  if (isSharedMenuLinkLoading || isVenueLevelMenuEditorEnabledLoading || isListingManagerBetaEnabledLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" color="blue" />
      </SpinnerContainer>
    );
  }

  const venueIsUsingOfferingPlatform = !!sharedMenuLink?.selection_id;
  const isSingleVenue = venueIsUsingOfferingPlatform && !sharedMenuLink?.selection_is_shared;
  const isSharedVenue = venueIsUsingOfferingPlatform && sharedMenuLink?.selection_is_shared;

  // Render nothing when loading POS integration status or if there was an error
  // loading it. There error is rendered on the menu root level so not needed here.
  if (!venuePosIntegrationStatus) {
    return null;
  }

  const canAccessTranslations =
    permissionUtils.hasMenuEditorPermission(permissions, externalAccessRights, venue) &&
    permissionUtils.hasMenuContentEditPermission(permissions, externalAccessRights, venue, venuePosIntegrationStatus);

  if (!canAccessTranslations) {
    return <Redirect to={`/venue/${venue.slug}`} />;
  }

  const isVlmaEnabled = isVenueLevelMenuEditorEnabled || isListingManagerBetaEnabled;

  if (isSingleVenue && isVlmaEnabled) {
    // Redirect to the new translations page (name is plural - translations)
    return (
      <VenueLevelTranslationsPage
        venue={venue}
        isSharedVenue={isSharedVenue}
        isSingleVenue={isSingleVenue}
        selectionId={sharedMenuLink?.selection_id}
      />
    );
  }

  return (
    <>
      <PageHeader />
      <Translation venue={venue} />
    </>
  );
};
